/**
 * 📦 كيان باقة القاعة
 *
 * يمثّل باقة قياسية واحدة، وهويتها مشتقة من:
 *   - sectionMode
 *   - dayType
 *   - season
 *   - hasIncludedServices
 *   - the active capacity range
 *
 * Single-section packages store only capacityMin/capacityMax. Both-section
 * packages additionally store the men/women ranges; total bounds are derived.
 *
 * لا يكتب هذا الكيان أسماء الحقول القديمة مثل services أو
 * servicesDetails أو priceHalalas.
 */

import { Money } from '@domain/value-objects/Money';
import { PackageKey } from '@domain/value-objects/PackageKey';
import {
  SectionMode,
  type SectionModeValue,
  type DayTypeValue,
  type SeasonValue,
} from '@domain/value-objects/PackageEnums';
import type {
  IncludedServices,
  MenIncludedServices,
  WomenIncludedServices,
} from '@domain/value-objects/IncludedServices';
import {
  assertIncludedServicesForWrite,
  hasIncludedServicesData,
} from '@domain/value-objects/IncludedServices';
import {
  ValidationError,
  BusinessRuleViolationError,
} from '@domain/errors/DomainErrors';

export interface PackageProps {
  readonly id: string;
  readonly key: string;
  readonly venueId: string;
  readonly name: string;
  readonly price: Money;
  readonly capacityMin: number;
  readonly capacityMax: number;
  readonly capacityMenMin?: number;
  readonly capacityMenMax?: number;
  readonly capacityWomenMin?: number;
  readonly capacityWomenMax?: number;
  readonly sectionMode: SectionModeValue;
  readonly dayType: DayTypeValue;
  readonly season: SeasonValue;
  readonly currency: 'SAR';
  readonly hasIncludedServices: boolean;
  readonly includedServicesText?: string;
  readonly includedServices?: IncludedServices;
  readonly includedServicesMenText?: string;
  readonly includedServicesMen?: MenIncludedServices;
  readonly includedServicesWomenText?: string;
  readonly includedServicesWomen?: WomenIncludedServices;
  readonly notes?: string;
  readonly updatedAt: Date;
  readonly createdAt: Date;
}

export interface CreatePackageProps {
  venueId: string;
  name: string;
  price: Money;
  capacityMin?: number | undefined;
  capacityMax?: number | undefined;
  capacityMenMin?: number | undefined;
  capacityMenMax?: number | undefined;
  capacityWomenMin?: number | undefined;
  capacityWomenMax?: number | undefined;
  sectionMode: SectionModeValue;
  dayType: DayTypeValue;
  season: SeasonValue;
  hasIncludedServices: boolean;
  includedServicesText?: string | undefined;
  includedServices?: IncludedServices | undefined;
  includedServicesMenText?: string | undefined;
  includedServicesMen?: MenIncludedServices | undefined;
  includedServicesWomenText?: string | undefined;
  includedServicesWomen?: WomenIncludedServices | undefined;
  notes?: string | undefined;
  updatedAt: Date;
  createdAt: Date;
}

export interface PersistedPackageProps extends CreatePackageProps {
  readonly key: string;
  readonly capacityMin: number;
  readonly capacityMax: number;
  readonly currency: 'SAR';
}

export class Package {
  private constructor(public readonly props: PackageProps) {}

  /**
   * ينشئ باقة جديدة، ويولّد المفتاح والسعة الإجمالية من حقول الهوية والسعة.
   */
  static create(input: CreatePackageProps): Package {
    const normalized = normalizeAndValidate(input);
    const key = PackageKey.build({
      sectionMode: normalized.sectionMode,
      dayType: normalized.dayType,
      season: normalized.season,
      hasIncludedServices: normalized.hasIncludedServices,
      capacityMin: normalized.capacityMin,
      capacityMax: normalized.capacityMax,
      ...(normalized.capacityMenMin !== undefined
        ? { capacityMenMin: normalized.capacityMenMin }
        : {}),
      ...(normalized.capacityMenMax !== undefined
        ? { capacityMenMax: normalized.capacityMenMax }
        : {}),
      ...(normalized.capacityWomenMin !== undefined
        ? { capacityWomenMin: normalized.capacityWomenMin }
        : {}),
      ...(normalized.capacityWomenMax !== undefined
        ? { capacityWomenMax: normalized.capacityWomenMax }
        : {}),
    }).value;

    return new Package({
      ...normalized,
      id: key,
      key,
      currency: 'SAR',
    });
  }

  /**
   * يعيد تكوين باقة من مستند موثوق بعد التحقق من الهوية والسعة المخزنتين.
   */
  static fromPersistence(
    documentId: string,
    input: PersistedPackageProps
  ): Package {
    const pkg = Package.create(input);
    if (documentId !== input.key || documentId !== pkg.key) {
      throw new ValidationError(
        `معرّف مستند الباقة "${documentId}" لا يطابق مفتاحها القياسي "${pkg.key}"`,
        { field: 'key', value: input.key }
      );
    }

    if (
      input.capacityMin !== pkg.props.capacityMin ||
      input.capacityMax !== pkg.props.capacityMax
    ) {
      throw new ValidationError(
        `نطاق السعة الإجمالي للباقة "${documentId}" لا يطابق سعات الأقسام`,
        { field: 'capacityMin', value: input.capacityMin }
      );
    }

    if (input.currency !== 'SAR') {
      throw new ValidationError('عملة الباقة يجب أن تكون SAR', {
        field: 'currency',
        value: input.currency,
      });
    }

    return new Package({
      ...pkg.props,
      id: documentId,
      key: documentId,
    });
  }

  get id(): string {
    return this.props.id;
  }

  get key(): string {
    return this.props.key;
  }

  /**
   * لقطة الباقة التي تحفظ داخل الحجز.
   *
   * تستخدم أسماء الحقول القياسية، وتحتفظ بالبيانات المنظمة عند توفرها.
   */
  toBookingSnapshot(): Record<string, unknown> {
    const p = this.props;
    return {
      key: p.key,
      venueId: p.venueId,
      name: p.name,
      price: p.price.toSAR(),
      capacityMin: p.capacityMin,
      capacityMax: p.capacityMax,
      ...(p.capacityMenMin !== undefined
        ? { capacityMenMin: p.capacityMenMin }
        : {}),
      ...(p.capacityMenMax !== undefined
        ? { capacityMenMax: p.capacityMenMax }
        : {}),
      ...(p.capacityWomenMin !== undefined
        ? { capacityWomenMin: p.capacityWomenMin }
        : {}),
      ...(p.capacityWomenMax !== undefined
        ? { capacityWomenMax: p.capacityWomenMax }
        : {}),
      sectionMode: p.sectionMode,
      dayType: p.dayType,
      season: p.season,
      currency: p.currency,
      hasIncludedServices: p.hasIncludedServices,
      ...(p.includedServicesText
        ? { includedServicesText: p.includedServicesText }
        : {}),
      ...(p.includedServices
        ? { includedServices: p.includedServices }
        : {}),
      ...(p.includedServicesMenText
        ? { includedServicesMenText: p.includedServicesMenText }
        : {}),
      ...(p.includedServicesMen
        ? { includedServicesMen: p.includedServicesMen }
        : {}),
      ...(p.includedServicesWomenText
        ? { includedServicesWomenText: p.includedServicesWomenText }
        : {}),
      ...(p.includedServicesWomen
        ? { includedServicesWomen: p.includedServicesWomen }
        : {}),
      ...(p.notes ? { notes: p.notes } : {}),
    };
  }

  /**
   * السعة الفعلية المستخدمة في العرض والملخصات.
   */
  get effectiveCapacity(): number {
    return this.props.capacityMax;
  }

  /**
   * يعيد نسخة جديدة بعد تطبيق التغييرات وإعادة التحقق من كل القواعد.
   */
  update(changes: Partial<CreatePackageProps>): Package {
    const current = this.props;
    const has = (field: keyof CreatePackageProps): boolean =>
      Object.prototype.hasOwnProperty.call(changes, field);

    const next: CreatePackageProps = {
      venueId: current.venueId,
      name: changes.name ?? current.name,
      price: changes.price ?? current.price,
      sectionMode: changes.sectionMode ?? current.sectionMode,
      dayType: changes.dayType ?? current.dayType,
      season: changes.season ?? current.season,
      hasIncludedServices:
        changes.hasIncludedServices ?? current.hasIncludedServices,
      createdAt: current.createdAt,
      updatedAt: changes.updatedAt ?? new Date(),
    };

    const rangeFields = [
      'capacityMin',
      'capacityMax',
      'capacityMenMin',
      'capacityMenMax',
      'capacityWomenMin',
      'capacityWomenMax',
    ] as const;
    const includedServicesText = has('includedServicesText')
      ? changes.includedServicesText
      : current.includedServicesText;
    const includedServices = has('includedServices')
      ? changes.includedServices
      : current.includedServices;
    const includedServicesMenText = has('includedServicesMenText')
      ? changes.includedServicesMenText
      : current.includedServicesMenText;
    const includedServicesMen = has('includedServicesMen')
      ? changes.includedServicesMen
      : current.includedServicesMen;
    const includedServicesWomenText = has('includedServicesWomenText')
      ? changes.includedServicesWomenText
      : current.includedServicesWomenText;
    const includedServicesWomen = has('includedServicesWomen')
      ? changes.includedServicesWomen
      : current.includedServicesWomen;
    const notes = has('notes') ? changes.notes : current.notes;

    for (const field of rangeFields) {
      const value = has(field) ? changes[field] : current[field];
      if (value !== undefined) next[field] = value;
    }
    if (includedServicesText !== undefined) {
      next.includedServicesText = includedServicesText;
    }
    if (includedServices !== undefined) {
      next.includedServices = includedServices;
    }
    if (includedServicesMenText !== undefined) {
      next.includedServicesMenText = includedServicesMenText;
    }
    if (includedServicesMen !== undefined) {
      next.includedServicesMen = includedServicesMen;
    }
    if (includedServicesWomenText !== undefined) {
      next.includedServicesWomenText = includedServicesWomenText;
    }
    if (includedServicesWomen !== undefined) {
      next.includedServicesWomen = includedServicesWomen;
    }
    if (notes !== undefined) next.notes = notes;

    return Package.create(next);
  }

  matches(filter: {
    sectionMode?: SectionModeValue;
    dayType?: DayTypeValue;
    season?: SeasonValue;
    hasIncludedServices?: boolean;
  }): boolean {
    if (filter.sectionMode && filter.sectionMode !== this.props.sectionMode) {
      return false;
    }
    if (filter.dayType && filter.dayType !== this.props.dayType) {
      return false;
    }
    if (filter.season && filter.season !== this.props.season) {
      return false;
    }
    if (
      filter.hasIncludedServices !== undefined &&
      filter.hasIncludedServices !== this.props.hasIncludedServices
    ) {
      return false;
    }
    return true;
  }

  /**
   * كلما زادت المطابقات الدقيقة، زادت أولوية الباقة في Smart Resolver.
   */
  matchScore(filter: {
    sectionMode: SectionModeValue;
    dayType: DayTypeValue;
    season: SeasonValue;
    hasIncludedServices: boolean;
  }): number {
    let score = 0;
    if (this.props.sectionMode === filter.sectionMode) score += 8;
    else if (this.props.sectionMode !== SectionMode.BOTH) return -1;

    if (this.props.dayType === filter.dayType) score += 4;
    else if (this.props.dayType !== 'all') return -1;

    if (this.props.season === filter.season) score += 2;
    else if (this.props.season !== 'all') return -1;

    if (
      this.props.hasIncludedServices === filter.hasIncludedServices
    ) {
      score += 1;
    } else {
      return -1;
    }

    return score;
  }
}

function normalizeAndValidate(
  input: CreatePackageProps
): Omit<PackageProps, 'id' | 'key' | 'currency'> {
  const name = input.name.trim().replace(/\s+/g, ' ');
  if (!name) {
    throw new ValidationError('اسم الباقة مطلوب', {
      field: 'name',
      value: input.name,
    });
  }
  if (name.length > 80) {
    throw new ValidationError('اسم الباقة يجب ألا يتجاوز 80 حرفاً', {
      field: 'name',
      value: input.name,
    });
  }

  const price = input.price.toSAR();
  if (!Number.isInteger(price) || price < 1) {
    throw new ValidationError(
      'السعر يجب أن يكون عدداً صحيحاً موجباً بالريال',
      { field: 'price', value: price }
    );
  }

  if (!input.venueId.trim()) {
    throw new ValidationError('venueId مطلوب', { field: 'venueId' });
  }

  const totalMin = optionalPositiveInteger(input.capacityMin, 'capacityMin');
  const totalMax = optionalPositiveInteger(input.capacityMax, 'capacityMax');
  const menMin = optionalPositiveInteger(
    input.capacityMenMin,
    'capacityMenMin'
  );
  const menMax = optionalPositiveInteger(
    input.capacityMenMax,
    'capacityMenMax'
  );
  const womenMin = optionalPositiveInteger(
    input.capacityWomenMin,
    'capacityWomenMin'
  );
  const womenMax = optionalPositiveInteger(
    input.capacityWomenMax,
    'capacityWomenMax'
  );

  let capacityMin: number;
  let capacityMax: number;
  let normalizedMenMin: number | undefined;
  let normalizedMenMax: number | undefined;
  let normalizedWomenMin: number | undefined;
  let normalizedWomenMax: number | undefined;

  switch (input.sectionMode) {
    case SectionMode.MEN:
      assertOmittedSectionRanges(input);
      [capacityMin, capacityMax] = requireCapacityRange(
        totalMin,
        totalMax,
        'capacity'
      );
      break;

    case SectionMode.WOMEN:
      assertOmittedSectionRanges(input);
      [capacityMin, capacityMax] = requireCapacityRange(
        totalMin,
        totalMax,
        'capacity'
      );
      break;

    case SectionMode.BOTH:
      [normalizedMenMin, normalizedMenMax] = requireCapacityRange(
        menMin,
        menMax,
        'capacityMen'
      );
      [normalizedWomenMin, normalizedWomenMax] = requireCapacityRange(
        womenMin,
        womenMax,
        'capacityWomen'
      );
      capacityMin = normalizedMenMin + normalizedWomenMin;
      capacityMax = normalizedMenMax + normalizedWomenMax;
      if (
        (totalMin !== undefined && totalMin !== capacityMin) ||
        (totalMax !== undefined && totalMax !== capacityMax)
      ) {
        throw new ValidationError(
          'نطاق السعة الإجمالي لا يطابق مجموع نطاقي الرجال والنساء',
          { field: 'capacityMin', value: totalMin }
        );
      }
      break;

    default:
      throw new ValidationError('نوع القسم غير صالح', {
        field: 'sectionMode',
        value: input.sectionMode,
      });
  }

  const includedServicesText = normalizeOptionalText(
    input.includedServicesText
  );
  const includedServicesMenText = normalizeOptionalText(
    input.includedServicesMenText
  );
  const includedServicesWomenText = normalizeOptionalText(
    input.includedServicesWomenText
  );
  const notes = normalizeOptionalText(input.notes);
  const includedServices = hasIncludedServicesData(
    input.includedServices
  )
    ? input.includedServices
    : undefined;
  const includedServicesMen = hasIncludedServicesData(
    input.includedServicesMen
  )
    ? input.includedServicesMen
    : undefined;
  const includedServicesWomen = hasIncludedServicesData(
    input.includedServicesWomen
  )
    ? input.includedServicesWomen
    : undefined;

  if (!input.hasIncludedServices) {
    if (
      includedServices ||
      includedServicesText ||
      includedServicesMen ||
      includedServicesMenText ||
      includedServicesWomen ||
      includedServicesWomenText
    ) {
      throw new BusinessRuleViolationError(
        'لا يمكن حفظ بيانات خدمات عندما تكون الباقة بدون خدمات',
        { field: 'includedServices' }
      );
    }
  } else if (input.sectionMode === SectionMode.BOTH) {
    if (includedServices || includedServicesText) {
      throw new BusinessRuleViolationError(
        'باقة القسمين يجب أن تستخدم خدمات رجال ونساء منفصلة',
        { field: 'includedServices' }
      );
    }
    if (
      !includedServicesMenText ||
      !includedServicesMen ||
      !includedServicesWomenText ||
      !includedServicesWomen
    ) {
      throw new BusinessRuleViolationError(
        'ملخص وتفاصيل الخدمات مطلوبان لكل من قسم الرجال والنساء',
        { field: 'includedServicesMen' }
      );
    }
    assertIncludedServicesForWrite(includedServicesMen, 'men', true);
    assertIncludedServicesForWrite(includedServicesWomen, 'women', true);
  } else {
    if (
      includedServicesMen ||
      includedServicesMenText ||
      includedServicesWomen ||
      includedServicesWomenText
    ) {
      throw new BusinessRuleViolationError(
        'باقة القسم الواحد يجب أن تستخدم includedServices فقط',
        { field: 'includedServicesMen' }
      );
    }
    if (!includedServicesText || !includedServices) {
      throw new BusinessRuleViolationError(
        'ملخص الخدمات وتفاصيلها مطلوبان للباقة الشاملة',
        { field: 'includedServices' }
      );
    }
    assertIncludedServicesForWrite(
      includedServices,
      input.sectionMode === SectionMode.MEN ? 'men' : 'women'
    );
  }

  return {
    venueId: input.venueId.trim(),
    name,
    price: input.price,
    capacityMin,
    capacityMax,
    ...(normalizedMenMin !== undefined
      ? { capacityMenMin: normalizedMenMin }
      : {}),
    ...(normalizedMenMax !== undefined
      ? { capacityMenMax: normalizedMenMax }
      : {}),
    ...(normalizedWomenMin !== undefined
      ? { capacityWomenMin: normalizedWomenMin }
      : {}),
    ...(normalizedWomenMax !== undefined
      ? { capacityWomenMax: normalizedWomenMax }
      : {}),
    sectionMode: input.sectionMode,
    dayType: input.dayType,
    season: input.season,
    hasIncludedServices: input.hasIncludedServices,
    ...(input.hasIncludedServices && includedServicesText
      ? { includedServicesText }
      : {}),
    ...(input.hasIncludedServices && includedServices
      ? { includedServices }
      : {}),
    ...(input.hasIncludedServices && includedServicesMenText
      ? { includedServicesMenText }
      : {}),
    ...(input.hasIncludedServices && includedServicesMen
      ? { includedServicesMen }
      : {}),
    ...(input.hasIncludedServices && includedServicesWomenText
      ? { includedServicesWomenText }
      : {}),
    ...(input.hasIncludedServices && includedServicesWomen
      ? { includedServicesWomen }
      : {}),
    ...(notes ? { notes } : {}),
    updatedAt: input.updatedAt,
    createdAt: input.createdAt,
  };
}

function optionalPositiveInteger(
  value: number | undefined,
  field: string
): number | undefined {
  if (value === undefined) return undefined;
  if (!Number.isInteger(value) || value < 1) {
    throw new ValidationError('السعة يجب أن تكون عدداً صحيحاً موجباً', {
      field,
      value,
    });
  }
  return value;
}

function requireCapacityRange(
  minimum: number | undefined,
  maximum: number | undefined,
  field: string
): [number, number] {
  if (minimum === undefined || maximum === undefined) {
    throw new ValidationError(`نطاق ${field} مطلوب`, {
      field: `${field}Min`,
      value: minimum,
    });
  }
  if (maximum - minimum < 10) {
    throw new ValidationError(
      `الحد الأعلى لـ ${field} يجب أن يزيد عن الحد الأدنى بـ 10 على الأقل`,
      { field: `${field}Min`, value: minimum }
    );
  }
  return [minimum, maximum];
}

function assertOmittedSectionRanges(input: CreatePackageProps): void {
  const sectionFields = [
    'capacityMenMin',
    'capacityMenMax',
    'capacityWomenMin',
    'capacityWomenMax',
  ] as const;
  const stored = sectionFields.find((field) => input[field] !== undefined);
  if (stored) {
    throw new ValidationError(
      'باقات القسم الواحد يجب أن تستخدم capacityMin وcapacityMax فقط',
      { field: stored, value: input[stored] }
    );
  }
}

function normalizeOptionalText(
  value: string | undefined
): string | undefined {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

export {
  ValidationError,
  BusinessRuleViolationError,
};
